import React, { useState, useEffect } from 'react';
import ApiService from '../../services/apiService';
import AdminLogin from './Login';
import HeadlineManager from './HeadlineManager';
import NotificationManager from './NotificationManager';
import SliderManager from './SliderManager';
import LibrarySliderManager from './LibrarySliderManager';
import LibraryGalleryManager from './LibraryGalleryManager';
import Navbar from '../Homepage/Navbar/Navbar';
import Footer from '../Homepage/Footer/Footer';
import './Dashboard.css';

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await ApiService.getCurrentUser();
      if (response.success && response.user.role === 'admin') {
        setUser(response.user);
      }
    } catch (error) {
      console.log('Not authenticated');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await ApiService.logout();
      setUser(null);
      setActiveSection('dashboard');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleCardClick = (section) => {
    setActiveSection(section);
  };

  const handleBackToDashboard = () => {
    setActiveSection('dashboard');
  };

  if (loading) {
    return (
      <div id="admin-dashboard-container">
        <div className="admin-loading">Loading Admin Dashboard</div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Navbar />
        <AdminLogin onLoginSuccess={handleLoginSuccess} />
        <Footer />
      </>
    );
  }

  const renderDashboardCards = () => (
    <div className="admin-dashboard-grid">
      <div className="admin-dashboard-card" onClick={() => handleCardClick('headlines')}>
        <div className="admin-card-header">
          <span className="admin-card-icon">📰</span>
          <h3 className="admin-card-title">Headlines</h3>
        </div>
        <div className="admin-card-body">
          <p className="admin-card-description">
            Manage university headlines and important announcements that appear on the homepage.
          </p>
          <button className="admin-card-button">Manage Headlines</button>
        </div>
      </div>

      <div className="admin-dashboard-card" onClick={() => handleCardClick('notifications')}>
        <div className="admin-card-header">
          <span className="admin-card-icon">🔔</span>
          <h3 className="admin-card-title">Notifications</h3>
        </div>
        <div className="admin-card-body">
          <p className="admin-card-description">
            Create and manage notifications for different categories like tenders, examinations, and circulars.
          </p>
          <button className="admin-card-button">Manage Notifications</button>
        </div>
      </div>

      <div className="admin-dashboard-card" onClick={() => handleCardClick('slider')}>
        <div className="admin-card-header">
          <span className="admin-card-icon">🖼️</span>
          <h3 className="admin-card-title">Image Slider</h3>
        </div>
        <div className="admin-card-body">
          <p className="admin-card-description">
            Upload and manage banner images that display in the homepage image slider carousel.
          </p>
          <button className="admin-card-button">Manage Slider</button>
        </div>
      </div>

      <div className="admin-dashboard-card" onClick={() => handleCardClick('library-slider')}>
        <div className="admin-card-header">
          <span className="admin-card-icon">📚</span>
          <h3 className="admin-card-title">Library Slider</h3>
        </div>
        <div className="admin-card-body">
          <p className="admin-card-description">
            Upload and manage library images that display in the library page slider carousel.
          </p>
          <button className="admin-card-button">Manage Library Slider</button>
        </div>
      </div>

      <div className="admin-dashboard-card" onClick={() => handleCardClick('library-gallery')}>
        <div className="admin-card-header">
          <span className="admin-card-icon">🖼️</span>
          <h3 className="admin-card-title">Library Gallery</h3>
        </div>
        <div className="admin-card-body">
          <p className="admin-card-description">
            Upload and manage library gallery images that display in the library page gallery section.
          </p>
          <button className="admin-card-button">Manage Library Gallery</button>
        </div>
      </div>
    </div>
  );

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'headlines':
        return (
          <div className="admin-manager-section">
            <div className="admin-manager-header">
              <h2>Headlines Management</h2>
              <button onClick={handleBackToDashboard} className="admin-card-button">
                ← Back to Dashboard
              </button>
            </div>
            <div className="admin-manager-content">
              <HeadlineManager />
            </div>
          </div>
        );
      case 'notifications':
        return (
          <div className="admin-manager-section">
            <div className="admin-manager-header">
              <h2>Notifications Management</h2>
              <button onClick={handleBackToDashboard} className="admin-card-button">
                ← Back to Dashboard
              </button>
            </div>
            <div className="admin-manager-content">
              <NotificationManager />
            </div>
          </div>
        );
      case 'slider':
        return (
          <div className="admin-manager-section">
            <div className="admin-manager-header">
              <h2>Slider Management</h2>
              <button onClick={handleBackToDashboard} className="admin-card-button">
                ← Back to Dashboard
              </button>
            </div>
            <div className="admin-manager-content">
              <SliderManager />
            </div>
          </div>
        );
      case 'library-slider':
        return (
          <div className="admin-manager-section">
            <div className="admin-manager-header">
              <h2>Library Slider Management</h2>
              <button onClick={handleBackToDashboard} className="admin-card-button">
                ← Back to Dashboard
              </button>
            </div>
            <div className="admin-manager-content">
              <LibrarySliderManager />
            </div>
          </div>
        );
      case 'library-gallery':
        return (
          <div className="admin-manager-section">
            <div className="admin-manager-header">
              <h2>Library Gallery Management</h2>
              <button onClick={handleBackToDashboard} className="admin-card-button">
                ← Back to Dashboard
              </button>
            </div>
            <div className="admin-manager-content">
              <LibraryGalleryManager />
            </div>
          </div>
        );
      default:
        return (
          <>
            <h2 style={{ textAlign: 'center', color: '#8B0000', marginBottom: '2rem', fontSize: '2rem' }}>
              Admin Dashboard
            </h2>
            {renderDashboardCards()}
          </>
        );
    }
  };

  return (
    <>
      <Navbar />
      <div id="admin-dashboard-container">
        <header className="admin-header">
          <h1>Rayalseema University - Admin Panel</h1>
          <div className="admin-user-info">
            <span>Welcome, {user.username}</span>
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </header>

        <main className="admin-content">
          {renderActiveSection()}
        </main>
      </div>
      <Footer />
    </>
  );
};

export default AdminDashboard;